import { getLevel1ById, getLevel2ById, getLevel3LabelById } from "../constants/catalog-levels";
import {
  computeTotalPrice,
  formValuesToPayload,
  slugifyClassName,
} from "../utils/class.utils";
import type { AdminClass, LinkableGrade } from "../types/class.types";

export const MOCK_LINKABLE_GRADES: LinkableGrade[] = [
  { id: "grade-a", name: "Grade A" },
  { id: "grade-b", name: "Grade B" },
  { id: "grade-c", name: "Grade C" },
];

const now = () => new Date().toISOString();

function row(
  partial: Omit<AdminClass, "createdAt" | "updatedAt"> & {
    createdAt?: string;
    updatedAt?: string;
  }
): AdminClass {
  const t = now();
  return {
    ...partial,
    createdAt: partial.createdAt ?? t,
    updatedAt: partial.updatedAt ?? t,
  };
}

export const SEED_ADMIN_CLASSES: AdminClass[] = [
  row({
    id: "cls-01",
    slug: "foundations-indian-classical-beginners",
    categorySlug: "hindustani-vocals",
    name: "Foundations of Indian Classical Music for Beginners - Beginner Level Course",
    description:
      "A structured introduction to Indian classical music for beginners covering swaras, rhythm, and basic ragas.",
    validityDays: 90,
    price: 4500,
    taxPercent: 18,
    totalPrice: 5310,
    levels: {
      level1Id: "vocals",
      level1Label: "Vocals",
      level2Id: "hindustani-vocals",
      level2Label: "Hindustani Vocals",
      level3Id: "age-above",
      level3Label: "10 years above",
    },
    benefits: [
      {
        id: "b1",
        title: "Build Musical Foundations",
        description: "Develop strong fundamentals in swaras, rhythm, and basic ragas.",
      },
    ],
    school: {
      schoolName: "Harmoni Music School",
      adminName: "School Admin",
      email: "admin@harmoni.com",
      contactNumber: "+91 98417 28747",
      linkedGradeIds: ["grade-a"],
    },
    studentsOnboarded: 10,
    isActive: true,
  }),
  row({
    id: "cls-02",
    slug: "carnatic-violin-basics",
    categorySlug: "carnatic-vocals",
    name: "Carnatic Violin Basics - Beginner Class",
    description: "Learn Carnatic violin posture, bowing, and beginner exercises.",
    validityDays: 180,
    price: 4000,
    taxPercent: 18,
    totalPrice: 4720,
    levels: {
      level1Id: "vocals",
      level1Label: "Vocals",
      level2Id: "carnatic-vocals",
      level2Label: "Carnatic Vocals",
      level3Id: "age-below",
      level3Label: "10 years below",
    },
    benefits: [{ id: "b1", title: "Technique", description: "Master bowing and fingering basics." }],
    school: {
      schoolName: "Temple Academy",
      adminName: "Maha Lakshmi",
      email: "musictemple@harmoni.com",
      contactNumber: "100-2222-9999",
      linkedGradeIds: ["grade-a", "grade-b"],
    },
    studentsOnboarded: 20,
    isActive: true,
  }),
  row({
    id: "cls-03",
    slug: "intro-carnatic-vocal",
    categorySlug: "carnatic-vocals",
    name: "Introduction to Carnatic Vocal Music",
    description: "Explore Carnatic vocal traditions with guided practice sessions.",
    validityDays: 60,
    price: 3000,
    taxPercent: 18,
    totalPrice: 3540,
    levels: {
      level1Id: "vocals",
      level1Label: "Vocals",
      level2Id: "carnatic-vocals",
      level2Label: "Carnatic Vocals",
      level3Id: "age-above",
      level3Label: "10 years above",
    },
    benefits: [{ id: "b1", title: "Vocal clarity", description: "Improve tone and pitch stability." }],
    school: {
      schoolName: "South Indian Music Hub",
      adminName: "Admin",
      email: "south@harmoni.com",
      contactNumber: "+91 90000 11111",
      linkedGradeIds: ["grade-b"],
    },
    studentsOnboarded: 10,
    isActive: true,
  }),
  row({
    id: "cls-04",
    slug: "basics-indian-classical-theory",
    categorySlug: "hindustani-vocals",
    name: "Basics of Indian Classical Music Theory",
    description: "Theory-focused class covering notation, tala, and raga structure.",
    validityDays: 90,
    price: 2500,
    taxPercent: 18,
    totalPrice: 2950,
    levels: {
      level1Id: "vocals",
      level1Label: "Vocals",
      level2Id: "hindustani-vocals",
      level2Label: "Hindustani Vocals",
      level3Id: "age-above-female",
      level3Label: "10 Years above - Female",
    },
    benefits: [{ id: "b1", title: "Theory mastery", description: "Understand raga and tala frameworks." }],
    school: {
      schoolName: "Harmoni Music School",
      adminName: "Theory Lead",
      email: "theory@harmoni.com",
      contactNumber: "+91 98417 28747",
      linkedGradeIds: ["grade-a"],
    },
    studentsOnboarded: 20,
    isActive: true,
  }),
  row({
    id: "cls-05",
    slug: "beginners-raga-swara",
    categorySlug: "hindustani-vocals",
    name: "Beginner's Guide to Raga & Swara Practice",
    description: "Daily swara exercises and introductory raga alap practice.",
    validityDays: 120,
    price: 3390,
    taxPercent: 18,
    totalPrice: 3999,
    levels: {
      level1Id: "vocals",
      level1Label: "Vocals",
      level2Id: "hindustani-vocals",
      level2Label: "Hindustani Vocals",
      level3Id: "age-above",
      level3Label: "10 years above",
    },
    benefits: [{ id: "b1", title: "Daily practice", description: "Structured riyaz plans included." }],
    school: {
      schoolName: "Raga Institute",
      adminName: "Coordinator",
      email: "raga@harmoni.com",
      contactNumber: "+91 98888 22222",
      linkedGradeIds: ["grade-c"],
    },
    studentsOnboarded: 10,
    isActive: true,
  }),
  row({
    id: "cls-06",
    slug: "intro-mridangam-beginners",
    categorySlug: "language-of-rhythm",
    name: "Introduction to Mridangam for Beginners",
    description: "Rhythm fundamentals on mridangam for new learners.",
    validityDays: 60,
    price: 3600,
    taxPercent: 18,
    totalPrice: 4250,
    levels: {
      level1Id: "hobby-educational",
      level1Label: "Hobby/ Educational",
      level2Id: "language-of-rhythm",
      level2Label: "Language Of Rhythm",
      level3Id: "age-below-male",
      level3Label: "10 years below - Male",
    },
    benefits: [{ id: "b1", title: "Rhythm basics", description: "Learn tala and hand techniques." }],
    school: {
      schoolName: "Percussion Arts",
      adminName: "Admin",
      email: "perc@harmoni.com",
      contactNumber: "+91 97777 33333",
      linkedGradeIds: ["grade-b", "grade-c"],
    },
    studentsOnboarded: 20,
    isActive: false,
  }),
  ...Array.from({ length: 12 }, (_, i) => {
    const n = i + 7;
    const price = 3000 + n * 100;
    const tax = 18;
    return row({
      id: `cls-${String(n).padStart(2, "0")}`,
      slug: `catalog-class-${n}`,
      categorySlug: "guitar",
      name: `Sample Catalog Class ${n}`,
      description: `Placeholder description for catalog class ${n}.`,
      validityDays: 90,
      price,
      taxPercent: tax,
      totalPrice: computeTotalPrice(price, tax),
      levels: {
        level1Id: "instrumental",
        level1Label: "Instrumental",
        level2Id: "guitar",
        level2Label: "Guitar",
        level3Id: "age-above",
        level3Label: "10 years above",
      },
      benefits: [
        {
          id: "b1",
          title: `Benefit ${n}`,
          description: "Sample benefit description for mock data.",
        },
      ],
      school: {
        schoolName: "Demo School",
        adminName: "Demo Admin",
        email: "demo@harmoni.com",
        contactNumber: "+91 90000 00000",
        linkedGradeIds: ["grade-a"],
      },
      studentsOnboarded: n % 2 === 0 ? 20 : 10,
      isActive: n % 3 !== 0,
    });
  }),
];

export async function buildLevelsFromForm(
  level1Id: string,
  level2Id: string,
  level3Id: string
) {
  const l1 = await getLevel1ById(level1Id);
  const l2 = await getLevel2ById(level1Id, level2Id);
  const level3Label = await getLevel3LabelById(level3Id);
  return {
    level1Id,
    level1Label: l1?.label ?? level1Id,
    level2Id,
    level2Label: l2?.label ?? level2Id,
    level3Id,
    level3Label,
  };
}

export async function categorySlugFromLevels(
  level1Id: string,
  level2Id: string
): Promise<string> {
  const l2 = await getLevel2ById(level1Id, level2Id);
  return l2?.categorySlug ?? level1Id;
}

export async function createClassFromForm(
  values: ReturnType<typeof formValuesToPayload>
): Promise<AdminClass> {
  const levels = await buildLevelsFromForm(
    values.level1Id,
    values.level2Id,
    values.level3Id
  );
  const slug = slugifyClassName(values.name);
  return row({
    id: `cls-${Date.now()}`,
    slug,
    categorySlug: await categorySlugFromLevels(values.level1Id, values.level2Id),
    name: values.name,
    description: values.description,
    validityDays: values.validityDays,
    price: values.price,
    taxPercent: values.taxPercent,
    totalPrice: values.totalPrice,
    levels,
    benefits: values.benefits,
    school: values.school,
    thumbnailUrl: values.thumbnailUrl,
    uploadId: values.uploadId,
    schoolId: values.schoolId,
    courseId: values.courseId,
    studentsOnboarded: 0,
    isActive: true,
  });
}
